/**
 * DR-43 — neighbourhood × specialty listing pages (`/s/tehran/<specialty>/<area>/`).
 *
 * The API (search-service) is the only thing that decides whether a page is an area page: it answers
 * `area` for `/s/<city>/<specialty>/<area>`, and the neighbourhoods are an ordinary backend filter group (`filters[].name === 'area'`,
 * rendered by the same machinery as gender/degree; its items and `reset_href` carry the page paths).
 * Everything here is inert when neither is in the response, so a response without them renders
 * exactly the page of today — that is also the rollback (switch the API side off, the pages 404 there).
 */

import { buildDoctorItemList, listingJsonLds, listingPath } from './buildListingSeoExperiment';

export type AreaInfo = {
  id: string;
  slug: string;
  name_fa: string;
  name_en?: string | null;
  district_no?: number | null;
  city_slug?: string;
  total?: number;
  /** false ⇒ the page works but is thin (too few doctors): `noindex, follow`, out of links and sitemap. */
  indexable?: boolean;
  /** The API's own directive for the page: `index, follow` or `noindex, follow`. */
  robots?: string;
};

/** The neighbourhood group of the response's `filters` (search-service: `{name:'area', type:'radio', items:[…], reset_href?}`). */
export type AreaFilterItem = { title: string; value: string; count?: number; href: string };

type Crumb = { name?: unknown };

const SLUG = /^[a-z0-9-]+$/;
const ORIGIN = 'https://www.paziresh24.com';

/** The path of a listing URL without query/hash, always ending in a slash (one implementation, shared with DR-41). */
export const cleanListingPath = listingPath;

export const isAreaInfo = (value: unknown): value is AreaInfo =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as AreaInfo).slug === 'string' &&
  SLUG.test((value as AreaInfo).slug) &&
  typeof (value as AreaInfo).name_fa === 'string' &&
  (value as AreaInfo).name_fa.trim() !== '';

/**
 * Query keys that only say where a visitor came from. They change nothing in the list, so they must not turn a
 * page into a «filtered variant»: no noindex (the canonical to the clean URL is enough) and no hidden neighbourhood row.
 */
const HARMLESS_QUERY_KEY = /^(utm_.*|gad_.*|mc_.*|pk_.*|version_code|ref|fbclid|gclid|gclsrc|dclid|msclkid|yclid|_ga|_gl|application|download_source)$/i;

const queryOf = (asPath: string) => new URLSearchParams(asPath.split('#')[0].split('?')[1] ?? '');

/** The part of the query that changes the list: filters, sort, text, page — everything that is not tracking. */
export const userQueryString = (asPath: string, { dropPage = false } = {}) => {
  const kept = new URLSearchParams();
  queryOf(asPath).forEach((value, key) => {
    if (HARMLESS_QUERY_KEY.test(key) || (dropPage && key === 'page')) return;
    kept.append(key, value);
  });
  return kept.toString();
};

export const hasUserQuery = (asPath: string) => userQueryString(asPath) !== '';

/** A filter the visitor can remove: not tracking, not the page number, not the default `freeturn=all`. */
export const hasActiveFilter = (asPath: string) => {
  const q = new URLSearchParams(userQueryString(asPath, { dropPage: true }));
  if (q.get('freeturn') === 'all') q.delete('freeturn');
  return q.toString() !== '';
};

/**
 * `noindex, follow` for a thin area page and for a real filtered/sorted/paged variant of an area page (tracking
 * parameters do not count: its canonical is the clean URL); otherwise the API's own directive when it sends a valid one;
 * otherwise undefined (= whatever the page does today).
 */
export const areaRobots = (area: AreaInfo | undefined, asPath = ''): string | undefined => {
  if (!area) return undefined;
  if (hasUserQuery(asPath) || area.indexable === false) return 'noindex, follow';
  return area.robots === 'index, follow' || area.robots === 'noindex, follow' ? area.robots : undefined;
};

/** An area page is its own canonical: never the parent listing, never a query-string variant. */
export const areaCanonicalUrl = (asPath: string) => `${ORIGIN}${cleanListingPath(asPath)}`;

/**
 * The × on the neighbourhood chip: the parent specialty page WITH the visitor's own filters (freeturn, sort, gender…),
 * minus the area segment, the page number and tracking parameters. `/s/tehran/dermatology/saadat-abad/?freeturn=today&utm_x=1&page=2`
 * → `/s/tehran/dermatology/?freeturn=today`.
 */
export const parentListingHref = (asPath: string) => {
  const qs = userQueryString(asPath, { dropPage: true });
  return `${parentListingPath(asPath)}${qs ? `?${qs}` : ''}`;
};

/** What to tell a visitor whose filter emptied a tiny neighbourhood — in the words of the filter they chose. */
export const areaEmptyTitle = (areaName: string, asPath: string, freeturnLabels: Record<string, string>) => {
  const freeturn = queryOf(asPath).get('freeturn');
  if (freeturn === 'today') return `امروز در ${areaName} نوبتی نیست`;
  // own keys only: `?freeturn=constructor` must not print a function
  if (freeturn && freeturn !== 'all' && Object.prototype.hasOwnProperty.call(freeturnLabels, freeturn))
    return `برای «${freeturnLabels[freeturn]}» در ${areaName} نوبتی پیدا نشد`;
  return hasActiveFilter(asPath) ? `با فیلترهای انتخاب‌شده در ${areaName} پزشکی پیدا نشد` : `در ${areaName} پزشکی پیدا نشد`;
};

/** Expertise (`exp-`) and free-text (`q-`) segments never get neighbourhood pages (DR-43 §95: group specialties only). */
const NON_GROUP_SEGMENT = /^(exp|q)-/;

/** `/s/<city>/<specialty>/<area>` as the route params of a page, or null. The specialty must be a group specialty. */
export const areaRouteOf = (params: unknown): { city: string; gexp: string; area: string } | null => {
  if (!Array.isArray(params) || params.length !== 3) return null;
  const [city, gexp, area] = params as string[];
  if (!([city, gexp, area].every(v => typeof v === 'string' && SLUG.test(v)))) return null;
  return NON_GROUP_SEGMENT.test(gexp) ? null : { city, gexp, area };
};

/** `/s/tehran/dermatology/saadat-abad/?page=2` → `/s/tehran/dermatology/`. */
export const parentListingPath = (asPath: string) => {
  const segments = cleanListingPath(asPath).split('/').filter(Boolean);
  return `/${segments.slice(0, -1).join('/')}/`;
};

const crumbName = (jsonld: unknown, indexFromEnd: number): string | null => {
  if (!Array.isArray(jsonld)) return null;
  const crumb = jsonld[jsonld.length - 1 - indexFromEnd] as Crumb | undefined;
  return typeof crumb?.name === 'string' && crumb.name.trim() ? crumb.name.trim() : null;
};

/**
 * Specialty and city names for the link texts. They come from the page's own breadcrumb JSON-LD
 * ([پذیرش۲۴, city, specialty] on the parent, [.., city, specialty, area] on an area page) so the
 * words are the CMS's, not invented here. Null when the shape is not the expected one.
 */
export const listingNames = (jsonld: unknown, isAreaPage: boolean, areaName?: string) => {
  // On an area page the last crumb must be the area itself, else the positions are not what we assume.
  if (isAreaPage && crumbName(jsonld, 0) !== (areaName ?? '').trim()) return null;
  const fromEnd = isAreaPage ? 1 : 0;
  const group = crumbName(jsonld, fromEnd);
  const city = crumbName(jsonld, fromEnd + 1);
  return group && city ? { group, city } : null;
};

const ITEM_HREF = /^\/s\/[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+\/(\?[^#\s]*)?$/;
const RESET_HREF = /^\/s\/[a-z0-9-]+\/[a-z0-9-]+\/(\?[^#\s]*)?$/;

type FilterGroupLike = { name?: unknown; items?: unknown; reset_href?: unknown };

const areaGroupOf = (filters: unknown): FilterGroupLike | undefined =>
  Array.isArray(filters) ? (filters as FilterGroupLike[]).find(f => f && f.name === 'area') : undefined;

/** The items the page may print: valid slug, a title, an href that stays on a listing path (never an off-site or odd link). */
export const areaFilterItems = (filters: unknown): AreaFilterItem[] => {
  const items = areaGroupOf(filters)?.items;
  if (!Array.isArray(items)) return [];
  const out: AreaFilterItem[] = [];
  for (const item of items as Partial<AreaFilterItem>[]) {
    if (!item || typeof item.value !== 'string' || !SLUG.test(item.value)) continue;
    if (typeof item.title !== 'string' || !item.title.trim()) continue;
    if (typeof item.href !== 'string' || !ITEM_HREF.test(item.href)) continue;
    const count = Number(item.count);
    out.push({ title: item.title.trim(), value: item.value, href: item.href, ...(Number.isFinite(count) && count > 0 && { count }) });
  }
  return out;
};

export type AreaRelatedLink = { title: string; href: string };
export type AreaRelatedLinks = { nearby: AreaRelatedLink[]; specialties: AreaRelatedLink[] };

const RELATED_CAP = 8;

const relatedList = (list: unknown, titleKey: 'name_fa' | 'name'): AreaRelatedLink[] => {
  if (!Array.isArray(list)) return [];
  const out: AreaRelatedLink[] = [];
  for (const item of list as Record<string, unknown>[]) {
    if (out.length >= RELATED_CAP) break;
    const title = item?.[titleKey];
    const href = item?.href;
    // a crawlable link to a listing page only: the clean path, never a query, an off-site address or another route
    if (typeof title !== 'string' || !title.trim() || typeof href !== 'string' || !ITEM_HREF.test(href) || href.includes('?')) continue;
    out.push({ title: title.trim(), href });
  }
  return out;
};

/**
 * `area_related` of an area-page response (search-service): the closest neighbourhoods for the same specialty and the same
 * neighbourhood in other specialties — indexable pages only, decided by the API. Everything printed is validated here.
 */
export const areaRelatedLinks = (raw: unknown): AreaRelatedLinks => {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return { nearby: relatedList(r.nearby, 'name_fa'), specialties: relatedList(r.specialties, 'name') };
};

/** Where deselecting the neighbourhood goes: the parent listing with the visitor's own query (area is a PATH filter). */
export const areaResetHref = (filters: unknown): string | undefined => {
  const href = areaGroupOf(filters)?.reset_href;
  return typeof href === 'string' && RESET_HREF.test(href) ? href : undefined;
};

/**
 * The CMS specialty crumb is sometimes already «پوست و مو در تهران» (or «… تهران»): `bare` is the specialty
 * without the city, `withCity` says the city exactly once. One rule for every text that needs either.
 */
export const specialtyWithCity = (group: string, city: string) => {
  const tail = new RegExp(`(\\s+در)?\\s+${city.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  const bare = group.replace(tail, '').trim() || group;
  return { bare, withCity: group.endsWith(city) ? group : `${group} ${city}` };
};

/** «بهترین دکتر پوست و مو در سعادت آباد تهران». */
export const areaItemListName = (jsonld: unknown, area: AreaInfo): string | null => {
  const names = listingNames(jsonld, true, area.name_fa);
  if (!names) return null;
  return `بهترین دکتر ${specialtyWithCity(names.group, names.city).bare} در ${area.name_fa} ${names.city}`;
};

/**
 * JSON-LD for the listing page: today's breadcrumb output, plus — on a clean area URL with doctors — an ItemList of
 * the first doctors (Physician + the card's own rating; see buildDoctorItemList). Never throws, never invents.
 */
export const listingPageJsonLds = ({
  jsonld,
  area,
  asPath,
  total,
  result,
}: {
  jsonld: unknown;
  area: AreaInfo | undefined;
  asPath: string;
  total: number | undefined;
  result: unknown[] | undefined;
}): object[] => {
  const base = listingJsonLds(jsonld);
  // a thin page is told `noindex`: it publishes no list claim either
  if (!area || area.indexable === false || asPath.includes('?') || !total) return base;
  const name = areaItemListName(jsonld, area);
  if (!name) return base;
  const itemList = buildDoctorItemList({ name, total, cards: (Array.isArray(result) ? result : []) as any });
  return itemList.itemListElement.length > 0 ? [...base, itemList] : base;
};
